import { DeliveryTeamMemberRole, IDeliveryItemDetailsDTO, IEventDetailsDTO } from '../../../../../api/events.dao';
import { utcToZonedTime } from '../../../../../infrastructure/utils/date-fns-overrides.utils';
import { getScheduledEmailTimeString } from '../../../../../infrastructure/utils/date.util';

const SYNC_ROLES = [DeliveryTeamMemberRole.PROJECT_MANAGER, DeliveryTeamMemberRole.CO_PROJECT_MANAGER];
const MAX_MANAGERS = 2;

const sortKeyOf = (member: IDeliveryItemDetailsDTO): string =>
    (member?.email || member?.username || '').toLowerCase();

const nameOf = (member: IDeliveryItemDetailsDTO): string => {
    if (!member) {
        return '';
    }
    return member.fullName || `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim();
};

const toDate = (value: Date | string | undefined | null): Date | null => {
    if (!value) {
        return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const toZonedDateTimeString = (dateTime: Date, eventTimezone: string): string =>
    String(utcToZonedTime(dateTime, eventTimezone));

const format = (
    value: Date | string | undefined | null,
    timeZone: string,
    toMixPanelString: (dateTime: Date, eventTimezone: string) => string,
): string => {
    const date = toDate(value);
    return date ? toMixPanelString(date, timeZone) : '';
};

const resolveSalesforceProjectManagers = (
    members: IDeliveryItemDetailsDTO[] = [],
): IDeliveryItemDetailsDTO[] =>
    SYNC_ROLES
        .reduce<IDeliveryItemDetailsDTO[]>((acc, role) => acc.concat(
            (members ?? [])
                .filter(member => !!member && member.deliveryTeamMemberRole === role)
                .sort((a, b) => sortKeyOf(a).localeCompare(sortKeyOf(b))),
        ), [])
        .slice(0, MAX_MANAGERS);

class SalesforceSharedFieldsTrackingMapper {
    public static toTrackingProperties(event: IEventDetailsDTO): Record<string, string> {
        const timeZone = event?.timeZone;
        const [projectManager, projectManager2] = resolveSalesforceProjectManagers(event?.deliveryTeamMembers);

        return {
            'Event Name': event?.name ?? '',
            'Start Date': format(event?.startDateTime, timeZone, toZonedDateTimeString),
            'End Date': format(event?.endDateTime, timeZone, toZonedDateTimeString),
            'Start Time': format(event?.startDateTime, timeZone, getScheduledEmailTimeString),
            'End Time': format(event?.endDateTime, timeZone, getScheduledEmailTimeString),
            'Region': event?.region ?? '',
            'TimeZone': timeZone ?? '',
            'Department': event?.department ?? '',
            'Project Manager': nameOf(projectManager),
            'Project Manager 2': nameOf(projectManager2),
        };
    }
}

export { SalesforceSharedFieldsTrackingMapper, resolveSalesforceProjectManagers };
